import { Injectable, inject } from "@angular/core";
import { AppActions, AppLoginInfo, AuthProfilesKeys, User } from "tht-impianti-cl";
import { BaseDataService } from "src/ancestors/base-data.service";
import { TokenStorageService } from "./token-storage.service";
import { JwtPayload } from "sharedclasses";
import { PermissionService } from "./permission.service";

export type BaseLoginInfo = AppLoginInfo;

/**
 * Identità dell'utente corrente e permessi, in forma leggibile per i componenti.
 *
 * I metodi `can*` sono scorciatoie che delegano tutte a `PermissionService.can(...)`: la regola
 * (ruolo + contesto) sta in `isActionAllowed` di `tht-impianti-cl`, la stessa funzione che il
 * backend usa per negare. Qui non c'è logica, solo nomi che si leggono bene in un template.
 *
 * Per un caso non coperto da questi metodi non se ne aggiunge uno: si usa la direttiva
 * `*appCan="AppActions.X"`, che non richiede di toccare questo file.
 */
@Injectable(
    {
        providedIn: "root"
    }
)
export class UserService extends BaseDataService {
    private tkStorage = inject(TokenStorageService);
    private permissions = inject(PermissionService);

    constructor() {
        super();
    }

    public setSelectedProfile() {
        throw new Error("Not implemented");
    }

    public getUserContextType() {
        return this.permissions.contextType();
    }

    public getUserAuth(): string[] {
        return this.getJwtPayload()?.auths ?? [];
    }

    public hasAuth(auth: string) {
        if (!this.getJwtPayload()?.auths) {
            return false;
        }
        return this.getJwtPayload().auths.includes(auth);
    }

    public hasProfile(profile: AuthProfilesKeys): boolean {
        const profiles = this.getJwtPayload()?.profiles as AuthProfilesKeys[] ?? [];
        return profiles.includes(profile);
    }

    public hasOnlyProfile(profile: AuthProfilesKeys): boolean {
        const profiles = this.getJwtPayload()?.profiles as AuthProfilesKeys[] ?? [];
        if (profiles.length > 1) return false;
        return profiles[0] === profile;
    }

    public getProfiles(): AuthProfilesKeys[] {
        const jwt = this.getJwtPayload();
        return (jwt?.profiles as AuthProfilesKeys[]) ?? [];
    }

    /* ------------------------------------------------------------------ */
    /* Anagrafiche                                                         */
    /* ------------------------------------------------------------------ */

    /** Pagina di ELENCO delle organizzazioni: è la voce di menu */
    public canReadOrganizationList(): boolean {
        return this.permissions.can(AppActions.READ_ORGANIZATION_LIST);
    }

    /** SCHEDA di un'organizzazione */
    public canReadOrganization(): boolean {
        return this.permissions.can(AppActions.READ_ORGANIZATION);
    }

    public canWriteOrganization(): boolean {
        return this.permissions.canAny(AppActions.UPDATE_ORGANIZATION, AppActions.UPDATE_ORGANIZATION_CONTACTS);
    }

    public canCreateOrganization(): boolean {
        return this.permissions.can(AppActions.CREATE_ORGANIZATION);
    }

    public canDeleteOrganization(): boolean {
        return this.permissions.can(AppActions.DELETE_ORGANIZATION);
    }

    /** Pagina di ELENCO degli impianti */
    public canReadFacilityList(): boolean {
        return this.permissions.can(AppActions.READ_FACILITY_LIST);
    }

    /** SCHEDA di un impianto */
    public canReadFacility(): boolean {
        return this.permissions.can(AppActions.READ_FACILITY);
    }

    /**
     * "Una delle due scritture sull'impianto".
     *
     * NON va usata per decidere se mostrare un pulsante: un pulsante è UNA operazione, e chi ha
     * `CREATE_FACILITY` senza `UPDATE_FACILITY` (o viceversa) vedrebbe anche l'altro, per poi
     * prendere 403. Serve dove la domanda è davvero "questo utente scrive gli impianti?", come la
     * pagina dei Tipi impianto, i cui endpoint riusano proprio le AUTH degli impianti.
     * Per i pulsanti: `canCreateFacility`, `canUpdateFacility`, `canDeleteFacility`.
     */
    public canWriteFacility(): boolean {
        return this.permissions.canAny(AppActions.CREATE_FACILITY, AppActions.UPDATE_FACILITY);
    }

    public canCreateFacility(): boolean {
        return this.permissions.can(AppActions.CREATE_FACILITY);
    }

    public canUpdateFacility(): boolean {
        return this.permissions.can(AppActions.UPDATE_FACILITY);
    }

    public canDeleteFacility(): boolean {
        return this.permissions.can(AppActions.DELETE_FACILITY);
    }

    /**
     * Cambiare lo STATO di un impianto, cioè validare il dato: è del Tecnico.
     *
     * Il Compilatore crea in Bozza e non può portare fuori da lì (§Compilatore dell'analisi). Il
     * form usa questa per decidere se la scelta dello stato è offerta o no: la stessa regola che il
     * backend applica in `FacilityService`, che senza questa AUTH forza `DRAFT` in creazione e
     * risponde 403 a un cambio di stato in modifica.
     */
    public canUpdateFacilityStatus(): boolean {
        return this.permissions.can(AppActions.UPDATE_FACILITY_STATUS);
    }

    /** Messa in manutenzione dell'impianto: è del Manutentore e dell'Admin, non di chi lo modifica */
    public canUpdateFacilityMaintenance(): boolean {
        return this.permissions.can(AppActions.UPDATE_FACILITY_MAINTENANCE);
    }

    /** Pagina di ELENCO dei componenti */
    public canReadFacilityComponentList(): boolean {
        return this.permissions.can(AppActions.READ_FACILITY_COMPONENT_LIST);
    }

    /** SCHEDA di un componente */
    public canReadFacilityComponent(): boolean {
        return this.permissions.can(AppActions.READ_FACILITY_COMPONENT);
    }

    /** Come `canWriteFacility`: non per i pulsanti, che sono una operazione ciascuno */
    public canWriteFacilityComponent(): boolean {
        return this.permissions.canAny(AppActions.CREATE_FACILITY_COMPONENT, AppActions.UPDATE_FACILITY_COMPONENT);
    }

    public canCreateFacilityComponent(): boolean {
        return this.permissions.can(AppActions.CREATE_FACILITY_COMPONENT);
    }

    public canUpdateFacilityComponent(): boolean {
        return this.permissions.can(AppActions.UPDATE_FACILITY_COMPONENT);
    }

    public canDeleteFacilityComponent(): boolean {
        return this.permissions.can(AppActions.DELETE_FACILITY_COMPONENT);
    }

    /** Validare un componente, cioè portarlo fuori dalla Bozza: è del Tecnico */
    public canUpdateFacilityComponentStatus(): boolean {
        return this.permissions.can(AppActions.UPDATE_FACILITY_COMPONENT_STATUS);
    }

    /** Pagina di ELENCO delle aree */
    public canReadAreaList(): boolean {
        return this.permissions.can(AppActions.READ_AREA_LIST);
    }

    /** SCHEDA di un'area */
    public canReadArea(): boolean {
        return this.permissions.can(AppActions.READ_AREA);
    }

    public canWriteArea(): boolean {
        return this.permissions.canAny(AppActions.CREATE_AREA, AppActions.UPDATE_AREA);
    }

    public canDeleteArea(): boolean {
        return this.permissions.can(AppActions.DELETE_AREA);
    }

    /** Pagina di ELENCO degli utenti */
    public canReadUserList(): boolean {
        return this.permissions.can(AppActions.READ_USER_LIST);
    }

    /** SCHEDA di un utente */
    public canReadUser(): boolean {
        return this.permissions.can(AppActions.READ_USER);
    }

    public canWriteUser(): boolean {
        return this.permissions.canAny(AppActions.CREATE_USER, AppActions.UPDATE_USER);
    }

    public canCreateUser(): boolean {
        return this.permissions.can(AppActions.CREATE_USER);
    }

    public canDeleteUser(): boolean {
        return this.permissions.can(AppActions.DELETE_USER);
    }

    /** Assegnare i ruoli è l'unica operazione con escalation di privilegi: ha un permesso a sé */
    public canAssignUserRoles(): boolean {
        return this.permissions.can(AppActions.ASSIGN_USER_ROLE);
    }

    /* ------------------------------------------------------------------ */
    /* Ticket                                                              */
    /* ------------------------------------------------------------------ */

    /** Pagina di ELENCO dei ticket, ricerca e filtri */
    public canReadTicketList(): boolean {
        return this.permissions.can(AppActions.READ_TICKET_LIST);
    }

    public canReadAllTickets(): boolean {
        return this.permissions.can(AppActions.READ_TICKET);
    }

    public canReadMyTickets(): boolean {
        return this.permissions.can(AppActions.READ_TICKET);
    }

    public canWriteAllTickets(): boolean {
        return this.permissions.can(AppActions.UPDATE_TICKET);
    }

    public canWriteMyTickets(): boolean {
        return this.permissions.can(AppActions.UPDATE_TICKET);
    }

    public canCreateTicket(): boolean {
        return this.permissions.can(AppActions.CREATE_TICKET);
    }

    public canDeleteTicket(): boolean {
        return this.permissions.can(AppActions.DELETE_TICKET);
    }

    public canAssignTicket(): boolean {
        return this.permissions.can(AppActions.ASSIGN_TICKET);
    }

    /**
     * Prendere in carico un ticket su di sé è un'azione con AUTH propria, distinta
     * dall'assegnazione a un altro utente: il Manutentore, per esempio, non ha né l'una né l'altra.
     */
    public canTakeChargeTicket(): boolean {
        return this.permissions.can(AppActions.TAKE_CHARGE_TICKET);
    }

    public canChangeTicketStatus(): boolean {
        return this.permissions.can(AppActions.UPDATE_TICKET_STATUS);
    }

    /**
     * Abbinare (o staccare) un contratto di manutenzione a un ticket GIÀ ESISTENTE.
     *
     * È un'operazione del Ticket Agent, con AUTH propria e distinta da `UPDATE_TICKET`: il
     * Manutentore corregge titolo, descrizione e priorità di un ticket ma non ne cambia il
     * contratto. Vale solo per la MODIFICA — in creazione il contratto arriva insieme al ticket,
     * sotto `CREATE_TICKET`.
     *
     * Nota: `canReadMaintenanceContract` NON è un sostituto. È un permesso di lettura, e usarlo per
     * decidere se un campo è modificabile mostra un controllo che il backend poi rifiuta.
     */
    public canAssignTicketMaintenanceContract(): boolean {
        return this.permissions.can(AppActions.ASSIGN_TICKET_MAINTENANCE_CONTRACT);
    }

    public canReadAllTicketComments(): boolean {
        return this.permissions.can(AppActions.READ_TICKET_COMMENT);
    }

    public canWriteAllTicketComments(): boolean {
        return this.permissions.can(AppActions.WRITE_TICKET_COMMENT);
    }

    public canReadMyTicketComments(): boolean {
        return this.permissions.can(AppActions.READ_TICKET_COMMENT);
    }

    public canWriteMyTicketComments(): boolean {
        return this.permissions.can(AppActions.WRITE_TICKET_COMMENT);
    }

    /**
     * Verifica se l'utente può cambiare tenant
     */
    public canChangeTenant(): boolean {
        return this.permissions.can(AppActions.CHANGE_TENANT);
    }

    /* ------------------------------------------------------------------ */
    /* Contratti e rapportini                                              */
    /* ------------------------------------------------------------------ */

    /** Pagina di ELENCO dei contratti */
    public canReadMaintenanceContractList(): boolean {
        return this.permissions.can(AppActions.READ_MAINTENANCE_CONTRACT_LIST);
    }

    /** SCHEDA di un contratto */
    public canReadMaintenanceContract(): boolean {
        return this.permissions.can(AppActions.READ_MAINTENANCE_CONTRACT);
    }

    public canWriteMaintenanceContract(): boolean {
        return this.permissions.canAny(AppActions.CREATE_MAINTENANCE_CONTRACT, AppActions.UPDATE_MAINTENANCE_CONTRACT);
    }

    public canWriteMaintenanceSection(): boolean {
        return this.permissions.can(AppActions.CREATE_MAINTENANCE_CONTRACT_SECTIONS);
    }

    /**
     * Blocco economico del contratto: budget, budget residuo, SAL, importi coperti.
     * È l'eccezione di CAMPO della tabella: il Manutentore vede il contratto ma non gli importi.
     */
    public canReadMaintenanceContractEconomics(): boolean {
        return this.permissions.can(AppActions.READ_MAINTENANCE_CONTRACT_ECONOMICS);
    }

    /* ------------------------------------------------------------------ */
    /* Manutenzioni interne                                                */
    /*                                                                     */
    /* Sono contratti legati a un'organizzazione CLIENTE, senza blocco      */
    /* economico e visibili nei soli contesti Cliente e Supervisore. I      */
    /* permessi per RUOLO sono gli stessi dei contratti (riusano le AUTH):  */
    /* quello che si aggiunge e' il vincolo di CONTESTO, e va composto      */
    /* SEMPRE, perche' ne' `can()` ne' `isActionAllowed` bastano da soli    */
    /* (vedi `PermissionService.canAccessInternalMaintenances`).            */
    /* ------------------------------------------------------------------ */

    /** Pagina di ELENCO delle manutenzioni interne */
    public canReadInternalMaintenanceList(): boolean {
        return this.permissions.can(AppActions.READ_INTERNAL_MAINTENANCE_LIST) && this.permissions.canAccessInternalMaintenances();
    }

    /** SCHEDA di una manutenzione interna */
    public canReadInternalMaintenance(): boolean {
        return this.permissions.can(AppActions.READ_INTERNAL_MAINTENANCE) && this.permissions.canAccessInternalMaintenances();
    }

    public canWriteInternalMaintenance(): boolean {
        return this.permissions.canAny(AppActions.CREATE_INTERNAL_MAINTENANCE, AppActions.UPDATE_INTERNAL_MAINTENANCE)
            && this.permissions.canAccessInternalMaintenances();
    }

    public canDeleteInternalMaintenance(): boolean {
        return this.permissions.can(AppActions.DELETE_INTERNAL_MAINTENANCE) && this.permissions.canAccessInternalMaintenances();
    }

    public canChangeInternalMaintenanceStatus(): boolean {
        return this.permissions.can(AppActions.UPDATE_INTERNAL_MAINTENANCE_STATUS) && this.permissions.canAccessInternalMaintenances();
    }

    /** ELENCO dei rapportini di costo di un contratto. */
    public canReadMaintenanceContractExpenseReportList(): boolean {
        return this.permissions.can(AppActions.READ_EXPENSE_REPORT_LIST);
    }

    /** Apertura di un rapportino di costo. */
    public canReadMaintenanceContractExpenseReports(): boolean {
        return this.permissions.can(AppActions.READ_EXPENSE_REPORT);
    }

    /** Modifica rapportini e voci (ticket / dettaglio contratto). */
    public canWriteMaintenanceContractExpenseReports(): boolean {
        return this.permissions.canAny(AppActions.WRITE_EXPENSE_REPORT, AppActions.WRITE_EXPENSE_REPORT_ITEMS);
    }

    /**
     * Download del report dell'impianto (libretto PDF ed export Excel).
     *
     * Legge `EXPORT_FACILITY_REPORT` e non piu' `EXPORT_FACILITY_BOOKLET`: erano due AUTH diverse e
     * il backend ha sempre preteso la prima, quindi il Compilatore — che ha solo il libretto —
     * vedeva il pulsante e riceveva un 401. Passa da `PermissionService.can` come gli altri metodi
     * di questa classe, cosi' la decisione resta di `isActionAllowed`, la stessa funzione che usa
     * il backend.
     */
    public canDownloadFacilityReport(): boolean {
        return this.permissions.can(AppActions.EXPORT_FACILITY_REPORT);
    }

    /**
     * Voci SOLO economiche del rapportino: spese generali, utile d'impresa, costi generali, oneri
     * di sicurezza, e il flag "non imputabile al cliente". Nota (*2) della tabella: sono esclusive
     * del ruolo Amministrativo.
     */
    public canWriteExpenseReportEconomics(): boolean {
        return this.permissions.can(AppActions.WRITE_EXPENSE_REPORT_ECONOMICS);
    }

    public canReadExpenseReportEconomics(): boolean {
        return this.permissions.can(AppActions.READ_EXPENSE_REPORT_ECONOMICS);
    }

    /* ------------------------------------------------------------------ */
    /* Identità                                                            */
    /* ------------------------------------------------------------------ */

    public getJwtPayload(): JwtPayload<User> {
        const USER = this.tkStorage.retriveToken(this.tokenKey);
        return USER.jwtPayload;
    }

    public getTokenExpireDate() {
        const USER = this.tkStorage.retriveToken(this.tokenKey);
        return USER.expireDate;
    }

    public isAdminOrSuperAdmin() {
        return this.hasProfile("ADMIN") || this.hasProfile("SUPER_ADMIN");
    }

    public isSuperAdmin() {
        return this.hasProfile("SUPER_ADMIN");
    }
    
    public isAdmin() {
        return this.hasProfile("ADMIN");
    }

    public isTicketAgent() {
        return this.hasProfile("TICKET_AGENT");
    }

    public isDataEntry() {
        return this.hasProfile("DATA_ENTRY");
    }
    
    // questo caso particolare si usa quando le varie entita',
    // per i compilatori, possono avere come unico stato modificabile
    // o inseribile, quello di bozza
    public isOnlyDataEntry() {
        return this.hasOnlyProfile("DATA_ENTRY");
    }

    public isTechnician() {
        return this.hasProfile("TECHNICIAN");
    }

    public isManteiner() {
        return this.hasProfile("MAINTAINER");
    }

    public isAdministrative() {
        return this.hasProfile("ADMINISTRATIVE");
    }

    public getUserProfile(): { userId: string, email: string, firstName: string, lastName: string, role: string } | null {
        const jwt = this.getJwtPayload();
        if (!jwt) return null;

        return {
            userId: jwt.user.userId,
            email: jwt.user.email,
            firstName: jwt.user.forename,
            lastName: jwt.user.surname,
            role: this.getProfiles().length > 0 ? this.getProfiles()[0] : "User"
        };
    }

    public getUserEmail() {
        const jwt = this.getJwtPayload();
        return jwt.user.email;
    }

    public getUserId() {
        const jwt = this.getJwtPayload();
        return jwt.user.userId;
    }

    /* ------------------------------------------------------------------ */
    /* Checklist                                                           */
    /* ------------------------------------------------------------------ */

    /** ELENCO delle checklist e delle compilazioni. */
    public canReadChecklistList(): boolean {
        return this.permissions.can(AppActions.READ_CHECKLIST_LIST);
    }

    /** DETTAGLIO di una checklist. */
    public canReadChecklist(): boolean {
        return this.permissions.can(AppActions.READ_CHECKLIST);
    }

    /** Creazione di una nuova checklist su un impianto. */
    public canCreateChecklist(): boolean {
        return this.permissions.can(AppActions.CREATE_CHECKLIST);
    }

    /** Modifica del contenuto di una checklist. */
    public canUpdateChecklist(): boolean {
        return this.permissions.can(AppActions.UPDATE_CHECKLIST);
    }

    /** Eliminazione di una checklist. */
    public canDeleteChecklist(): boolean {
        return this.permissions.can(AppActions.DELETE_CHECKLIST);
    }

    /** Lettura delle compilazioni: stessa visibilità delle checklist. */
    public canReadChecklistSubmission(): boolean {
        return this.permissions.can(AppActions.READ_CHECKLIST);
    }

    /** Creazione di una compilazione e salvataggio delle risposte. */
    public canFillChecklistSubmission(): boolean {
        return this.permissions.can(AppActions.FILL_CHECKLIST);
    }

    /** Condivisione col cliente di una compilazione completata o verificata. */
    public canShareChecklistSubmission(): boolean {
        return this.permissions.can(AppActions.SHARE_CHECKLIST_SUBMISSION);
    }

    /**
     * Verifica tecnica di una compilazione completata (COMPLETED -> VERIFIED).
     *
     * Gemella di `canUpdateCompletedChecklistSubmission`: sono due azioni distinte sulla stessa
     * AUTH, perché sono due gesti dello stesso ruolo — correggere un documento chiuso e attestarlo.
     */
    public canVerifyChecklistSubmission(): boolean {
        return this.permissions.can(AppActions.VERIFY_CHECKLIST_SUBMISSION);
    }

    /**
     * Modifica delle risposte di una compilazione già COMPLETATA (o VERIFICATA).
     *
     * È l'unica deroga all'immutabilità di una compilazione chiusa, e vale solo fino alla
     * condivisione: una PUBLISHED non la modifica nessuno. La regola gemella nel backend è
     * `ChecklistSubmissionService.requireEditableSubmission`, che sceglie il permesso in base allo
     * STATO della riga: se si cambia una va cambiata l'altra, o il pannello si apre in modifica e
     * il salvataggio risponde 401.
     */
    public canUpdateCompletedChecklistSubmission(): boolean {
        return this.permissions.can(AppActions.UPDATE_COMPLETED_CHECKLIST_SUBMISSION);
    }

    /**
     * Eliminazione di una compilazione.
     *
     * Dipende dal solo permesso, e non più dalla riga: una compilazione — bozza compresa — è il
     * documento di controllo di quel ticket, condiviso fra tutti gli utenti che lo compilano, non
     * il foglio personale di chi l'ha aperto per primo. Non esiste quindi la deroga "l'autore
     * butta la propria bozza", che era anche irraggiungibile: chi compila (Compilatore, Agente
     * ticket, Manutentore) non ha `DELETE_CHECKLIST_SUBMISSION`, quindi vedeva la voce di menu e
     * poi prendeva 403 dal controller.
     * La regola è la stessa che applica `ChecklistSubmissionService.requireDeletableSubmission`:
     * se si cambia una va cambiata l'altra, altrimenti la voce di menu compare e la chiamata
     * risponde 403.
     */
    public canDeleteChecklistSubmission(): boolean {
        return this.permissions.can(AppActions.DELETE_CHECKLIST_SUBMISSION);
    }

    /**
     * Eliminazione di UNA compilazione, guardando anche che cosa c'è dentro.
     *
     * È la gemella completa di `ChecklistSubmissionService.requireDeletableSubmission`, e la
     * differenza rispetto al metodo qui sopra è l'eccezione della BOZZA VUOTA: una compilazione
     * senza risposte non è un documento ma solo LA SCELTA di quale checklist compilare su quel
     * ticket — scelta che ora si materializza subito, spesso per mano di qualcuno (il Ticket
     * Agent) che poi non compilerà — e chi sceglie deve poterla correggere. Per quella basta
     * `FILL_CHECKLIST`; per tutto il resto serve `DELETE_CHECKLIST_SUBMISSION`.
     *
     * ATTENZIONE, unico punto in cui le due gemelle non coincidono al 100%: qui "vuota" si legge
     * da `answeredCount`, che conta gli elementi con un ESITO, mentre il server conta le RIGHE di
     * risposta. Una bozza in cui qualcuno ha scritto solo una nota o allegato solo una foto, senza
     * rispondere, ha `answeredCount = 0` ma delle righe: il pulsante compare e la chiamata risponde
     * 401 con il suo messaggio. È il verso giusto in cui sbagliare — non si cancella mai qualcosa
     * a sorpresa — e l'elenco non porta le risposte, quindi l'alternativa sarebbe una chiamata in
     * più su ogni riga.
     *
     * @param submission - Riga della compilazione, dall'elenco
     */
    public canDeleteChecklistSubmissionRow(submission: { status?: string, answeredCount?: number } | null | undefined): boolean {
        if (this.canDeleteChecklistSubmission()) {
            return true;
        }
        const isEmptyDraft = submission?.status === "DRAFT" && (submission?.answeredCount ?? 0) === 0;
        return isEmptyDraft && this.canFillChecklistSubmission();
    }

    /** Accesso alla pagina delle checklist condivise. */
    public canViewSharedChecklists(): boolean {
        return this.permissions.can(AppActions.VIEW_SHARED_CHECKLISTS);
    }

    /** Apertura di un ticket a partire da una anomalia di una checklist condivisa. */
    public canOpenTicketFromAnomaly(): boolean {
        return this.permissions.can(AppActions.OPEN_TICKET_FROM_ANOMALY);
    }

    /* ------------------------------------------------------------------ */
    /* Storico delle entità                                                */
    /* ------------------------------------------------------------------ */

    /*
     * Lo storico è TRACCIAMENTO, non dato operativo: ha un permesso proprio per entità,
     * indipendente dalla lettura della scheda. Per ora è concesso ai soli Admin e SuperAdmin,
     * quindi per tutti gli altri ruoli la sidebar dello storico non compare — e la chiamata non
     * viene nemmeno fatta.
     */

    public canReadOrganizationHistory(): boolean {
        return this.permissions.can(AppActions.READ_ORGANIZATION_HISTORY);
    }

    public canReadOrganizationUsers(): boolean {
        return this.permissions.can(AppActions.READ_ORGANIZATION_USERS);
    }

    /**
     * Storico di un utente, e anche il PROPRIO nella pagina Account: è la stessa AUTH.
     * Conseguenza voluta: con l'assegnazione attuale la scheda "Cronologia" della pagina Account
     * compare solo ad Admin e SuperAdmin.
     */
    public canReadUserHistory(): boolean {
        return this.permissions.can(AppActions.READ_USER_HISTORY);
    }

    /** Storico dell'impianto: copre l'intera sidebar, storico stati compreso */
    public canReadFacilityHistory(): boolean {
        return this.permissions.can(AppActions.READ_FACILITY_HISTORY);
    }

    /** Storico del componente: audit, stati e valori */
    public canReadFacilityComponentHistory(): boolean {
        return this.permissions.can(AppActions.READ_FACILITY_COMPONENT_HISTORY);
    }

    public canReadTicketHistory(): boolean {
        return this.permissions.can(AppActions.READ_TICKET_HISTORY);
    }

    public canReadChecklistHistory(): boolean {
        return this.permissions.can(AppActions.READ_CHECKLIST_HISTORY);
    }

    /**
     * L'utente opera in sola lettura (organizzazione di tipo Supervisore).
     * Comodo per mostrare un avviso o disabilitare un intero form in un colpo solo.
     */
    public isReadOnlyContext(): boolean {
        return this.permissions.isReadOnly();
    }
}
